/**
 * Minimal API helper.
 *
 * - Always calls the backend under the `/api` prefix.
 * - Throws a JS Error that also carries the parsed backend error payload (if any).
 * - The backend error shape is: { error: { code, message, request_id, details? } }
 *
 * NOTE: In docker-compose, the frontend container proxies `/api` to the API container.
 */

export type ApiError = {
  error: {
    code: string;
    message: string;
    request_id?: string;
    details?: any;
  };
};

type RefreshHandler = (failedToken?: string | null) => Promise<string | null>;

let refreshHandler: RefreshHandler | null = null;

export function registerAuthRefreshHandler(handler: RefreshHandler | null) {
  refreshHandler = handler;
}

export async function apiFetch<T>(
  path: string,
  opts: RequestInit = {},
  token?: string | null,
  skipAuthRetry = false
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers as any),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(path.startsWith("/api") ? path : `/api${path}`, {
    ...opts,
    headers,
  });

  if (!res.ok) {
    // Only retry on authentication failures. A 403 is usually an authorization
    // decision (e.g., reviewer/frozen architecture) and should not trigger refresh.
    if (!skipAuthRetry && res.status === 401 && refreshHandler && token) {
      try {
        const newToken = await refreshHandler(token);
        if (newToken) {
          return apiFetch<T>(path, opts, newToken, true);
        }
      } catch {
        // ignore and fall through to error handling
      }
    }

    let body: any = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const safeSerialize = (value: any): string => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    };
    const detailEntries = Array.isArray(body?.detail) ? body.detail : [];
    const detailMessage =
      detailEntries.length > 0
        ? detailEntries.map((detail) => detail?.msg ?? safeSerialize(detail)).join("; ")
        : null;
    const errorPayload = body?.error ?? body?.detail?.error ?? body?.detail;
    const fallbackMessage =
      errorPayload?.message ||
      (typeof body?.detail === "string" ? body.detail : null) ||
      detailMessage ||
      res.statusText ||
      "Request failed";

    const err: ApiError =
      errorPayload
        ? {
            error: {
              code: errorPayload.code ?? "HTTP_ERROR",
              message: errorPayload.message ?? fallbackMessage,
              details: errorPayload.details ?? body?.details ?? {},
            },
          }
        : { error: { code: "HTTP_ERROR", message: fallbackMessage, details: body } };

    throw Object.assign(new Error(err?.error?.message ?? fallbackMessage), { status: res.status, err });
  }
  const text = await res.text();
  if (!text || !text.trim()) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export function extractApiErrorMessage(error: any, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (error.err?.error?.message) return error.err.error.message;
  if (error.message) return error.message;
  if (error.err?.error) return String(error.err.error);
  return fallback;
}

export type User = {
  id: number;
  username: string;
  email: string | null;
  managed_by_idp: boolean;
  idp_provider?: string | null;
  idp_subject?: string | null;
  roles: string[];
  is_active: boolean;
  created_at: string;
  full_name: string | null;
  blocked_at?: string | null;
  blocked_by?: string | null;
  blocked_reason?: string | null;
  must_change_password: boolean;
};

export type UserCreatePayload = {
  username: string;
  email?: string | null;
  managed_by_idp?: boolean;
  idp_provider?: string | null;
  idp_subject?: string | null;
  password: string;
  roles: string[];
  is_active?: boolean;
  full_name?: string;
};

export type UserUpdatePayload = {
  username?: string;
  email?: string | null;
  managed_by_idp?: boolean;
  idp_provider?: string | null;
  idp_subject?: string | null;
  password?: string;
  roles?: string[];
  is_active?: boolean;
  full_name?: string;
  blocked_at?: string | null;
  blocked_by?: string | null;
  blocked_reason?: string | null;
};

export async function listUsers(accessToken: string): Promise<User[]> {
  return apiFetch<User[]>("/api/users", { method: "GET" }, accessToken);
}

export async function getUser(accessToken: string, userId: number): Promise<User> {
  return apiFetch<User>(`/api/users/${userId}`, { method: "GET" }, accessToken);
}

export async function createUser(accessToken: string, payload: UserCreatePayload): Promise<User> {
  return apiFetch<User>(
    "/api/users",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    accessToken
  );
}

export async function updateUser(accessToken: string, userId: number, payload: UserUpdatePayload): Promise<User> {
  return apiFetch<User>(
    `/api/users/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
    accessToken
  );
}

export async function activateUser(accessToken: string, userId: number): Promise<User> {
  return apiFetch<User>(`/api/users/${userId}/activate`, { method: "POST" }, accessToken);
}

export async function deactivateUser(accessToken: string, userId: number): Promise<User> {
  return apiFetch<User>(`/api/users/${userId}/deactivate`, { method: "POST" }, accessToken);
}

export async function resetUserTotp(accessToken: string, userId: number): Promise<User> {
  return apiFetch<User>(`/api/users/${userId}/reset-totp`, { method: "POST" }, accessToken);
}

export async function resetUserPassword(accessToken: string, userId: number): Promise<{ temporary_password: string }> {
  return apiFetch<{ temporary_password: string }>(`/api/users/${userId}/reset-password`, { method: "POST" }, accessToken);
}

export async function softDeleteAssessment(
  accessToken: string,
  assessmentId: number
): Promise<{ removed: boolean }> {
  return apiFetch<{ removed: boolean }>(
    `/api/assessments/${assessmentId}/delete`,
    {
      method: "POST",
    },
    accessToken
  );
}

export async function restoreAssessment(
  accessToken: string,
  assessmentId: number
): Promise<{ removed: boolean }> {
  return apiFetch<{ removed: boolean }>(
    `/api/assessments/${assessmentId}/restore`,
    {
      method: "POST",
    },
    accessToken
  );
}

export type ReportArtifact = {
  id: number;
  assessment_id: number;
  filename: string;
  prepared_by: string;
  reviewed_by: string;
  approved_by: string;
  generated_by: string | null;
  generated_at: string;
  size_bytes: number;
  content_type: string;
};

export async function listReports(accessToken: string, assessmentId: number): Promise<ReportArtifact[]> {
  return apiFetch<ReportArtifact[]>(
    `/api/assessments/${assessmentId}/reports`,
    { method: "GET" },
    accessToken
  );
}

export type AssessmentTagsOut = {
  tags: string[];
};

export type AssessmentTagDictionaryOut = {
  id: number;
  name: string;
  color: string | null;
};

export type AssessmentTagDictionaryCreatePayload = {
  name: string;
  color: string;
};

export type AssessmentTagDictionaryUpdatePayload = {
  name?: string;
  color?: string;
};

export async function listAssessmentTags(accessToken: string, assessmentId: number): Promise<string[]> {
  const response = await apiFetch<AssessmentTagsOut>(
    `/api/assessments/${assessmentId}/assessment-tags`,
    { method: "GET" },
    accessToken
  );
  return response.tags ?? [];
}

export async function saveAssessmentTags(
  accessToken: string,
  assessmentId: number,
  tags: string[]
): Promise<string[]> {
  const response = await apiFetch<AssessmentTagsOut>(
    `/api/assessments/${assessmentId}/assessment-tags`,
    { method: "PUT", body: JSON.stringify({ tags }) },
    accessToken
  );
  return response.tags ?? [];
}

export async function listAssessmentTagDictionary(
  accessToken: string,
  assessmentId: number
): Promise<AssessmentTagDictionaryOut[]> {
  return apiFetch<AssessmentTagDictionaryOut[]>(
    `/api/assessments/${assessmentId}/tags/details`,
    { method: "GET" },
    accessToken
  );
}

export async function createAssessmentTagDictionaryEntry(
  accessToken: string,
  assessmentId: number,
  payload: AssessmentTagDictionaryCreatePayload
): Promise<AssessmentTagDictionaryOut> {
  return apiFetch<AssessmentTagDictionaryOut>(
    `/api/assessments/${assessmentId}/tags`,
    { method: "POST", body: JSON.stringify(payload) },
    accessToken
  );
}

export async function updateAssessmentTagDictionaryEntry(
  accessToken: string,
  assessmentId: number,
  tagId: number,
  payload: AssessmentTagDictionaryUpdatePayload
): Promise<AssessmentTagDictionaryOut> {
  return apiFetch<AssessmentTagDictionaryOut>(
    `/api/assessments/${assessmentId}/tags/${tagId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
    accessToken
  );
}

export async function deleteAssessmentTagDictionaryEntry(
  accessToken: string,
  assessmentId: number,
  tagId: number
): Promise<void> {
  await apiFetch<void>(`/api/assessments/${assessmentId}/tags/${tagId}`, { method: "DELETE" }, accessToken);
}
